import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Phone, Landmark, ExternalLink, ShieldAlert, List, Map as MapIcon } from "lucide-react";
import { PageHeader, Spinner, Pill } from "../components/common";
import MapView from "../components/MapView";
import DocumentChecker from "../components/DocumentChecker";
import { SITE } from "../lib/site";
import api from "../lib/api";

export const MISSION_TYPE_LABELS = {
  embassy: "Embassy",
  high_commission: "High Commission",
  consulate_general: "Consulate General",
  consulate: "Consulate",
  honorary_consulate: "Honorary Consulate",
  visa_center: "Visa Application Centre",
};

export const LINK_LABELS = {
  visa_info: "Visa Information",
  evisa: "Official eVisa Portal",
  eta: "Electronic Travel Authorisation",
  immigration: "Immigration Authority",
  customs: "Customs",
  advisory: "Travel Advisories",
  passport: "Passport Services",
  mfa: "Ministry of Foreign Affairs",
};

export function Disclaimer({ text }) {
  if (!text) return null;
  return (
    <div className="border border-amber-300 bg-amber-50 p-4 flex gap-3 text-sm text-amber-900" data-testid="visa-disclaimer">
      <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
      <p>{text}</p>
    </div>
  );
}

export function MissionCard({ m }) {
  return (
    <div className="border border-zinc-300 bg-white p-5" data-testid={`mission-${m.id}`}>
      <div className="flex items-start justify-between gap-2">
        <Link to={`/visas/missions/${m.id}`} data-testid={`mission-link-${m.id}`}
          className="font-heading font-700 text-base leading-snug hover:text-[#0e7490] transition-colors">{m.name}</Link>
        <Pill tone="blue">{MISSION_TYPE_LABELS[m.mission_type] || m.mission_type}</Pill>
      </div>
      <div className="text-sm text-muted-foreground mt-2 flex items-start gap-1.5">
        <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{m.address}, {m.city}, {m.host_country}</span>
      </div>
      {m.phone && (
        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5 shrink-0" />{m.phone}
        </div>
      )}
      {m.jurisdiction && (
        <div className="text-xs text-zinc-600 mt-2"><span className="font-700">Jurisdiction:</span> {m.jurisdiction}</div>
      )}
      <div className="flex items-center justify-between mt-3 text-xs">
        <span className="text-muted-foreground">Verified {m.last_verified}</span>
        {m.website && (
          <a href={m.website} target="_blank" rel="noreferrer" className="font-700 text-primary hover:underline flex items-center gap-1">
            Official website <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}

const TABS = [
  ["countries", "Country Directory"],
  ["finder", "Embassy & Consulate Finder"],
  ["checker", "Trip Document Checker"],
];

export default function VisasHub() {
  const [tab, setTab] = useState("countries");
  const [overview, setOverview] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/visas/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/visas/countries").then((r) => setCountries(r.data.items)).catch(() => {});
  }, []);

  return (
    <div data-testid="visas-hub-page">
      <PageHeader eyebrow={`${SITE.name} · Passports & Visas`} title="Passports, Visas & Embassies" entityCode="VISAS"
        subtitle="Official government visa resources, worldwide embassies, consulates and high commissions — plus tools to find the right mission and prepare your travel documents." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {overview && (
          <div className="flex flex-wrap gap-3 mb-8" data-testid="visas-stats">
            {[["Countries", overview.countries], ["Diplomatic Missions", overview.missions]].map(([label, value]) => (
              <div key={label} className="border border-zinc-300 bg-white px-5 py-3 min-w-[140px]">
                <div className="font-heading font-900 text-2xl text-[#0e7490]">{value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`visas-tab-${id}`}
              className={`px-4 py-2 text-sm font-700 border transition-colors ${tab === id ? "bg-[#0e7490] border-[#0e7490] text-white" : "bg-white border-zinc-300 text-zinc-700"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "countries" && <CountryDirectory countries={countries} />}
        {tab === "finder" && <EmbassyFinder countries={countries} />}
        {tab === "checker" && <DocumentChecker countries={countries} />}

        <div className="mt-10">
          <Disclaimer text={overview?.disclaimer} />
        </div>
      </div>
    </div>
  );
}

function CountryDirectory({ countries }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return countries;
    const s = q.trim().toLowerCase();
    return countries.filter((c) => c.name.toLowerCase().includes(s) || c.iso2.toLowerCase() === s || (c.capital || "").toLowerCase().includes(s));
  }, [countries, q]);

  if (!countries.length) return <Spinner />;
  return (
    <div data-testid="visas-country-directory">
      <div className="relative mb-6 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="visas-country-search"
          placeholder="Search by country, code or capital…"
          className="w-full border border-zinc-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Link key={c.id} to={`/visas/${c.id}`} data-testid={`visas-country-${c.id}`}
            className="border border-zinc-300 bg-white p-5 hover:border-[#0e7490] hover:-translate-y-px transition-[transform,border-color]">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-heading font-700 text-lg leading-snug">{c.name}</h3>
              <span className="text-xs font-mono uppercase border border-zinc-300 px-1.5 py-0.5 text-zinc-600">{c.iso2}</span>
            </div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Landmark className="w-3.5 h-3.5" />{c.capital} · {c.region}</div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(c.entry_programs || []).slice(0, 2).map((p) => <Pill key={p} tone="green">{p}</Pill>)}
              {c.schengen && <Pill tone="blue">Schengen</Pill>}
            </div>
          </Link>
        ))}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No matches yet — country coverage is expanding continuously.</p>}
      </div>
    </div>
  );
}

const CountrySelect = ({ label, value, onChange, countries, testid }) => (
  <label className="block flex-1 min-w-[220px]">
    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none">
      <option value="">Select a country…</option>
      {countries.map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
    </select>
  </label>
);

export function MissionMap({ missions, height = "440px" }) {
  const points = missions.filter((m) => m.lat != null && m.lon != null);
  if (!points.length) return <p className="text-sm text-muted-foreground py-8 text-center">No map coordinates available for these missions yet.</p>;
  const center = [points.reduce((s, m) => s + m.lat, 0) / points.length,
                  points.reduce((s, m) => s + m.lon, 0) / points.length];
  const zoom = points.length === 1 ? 12 : 4;
  return (
    <div data-testid="embassy-map">
      <MapView center={center} zoom={zoom} height={height}
        markers={points.map((m) => ({
          lat: m.lat, lon: m.lon, radius: 7,
          popup: `${m.name} — ${m.address || m.city}${m.website ? "" : ""}`,
        }))} />
      <p className="text-xs text-muted-foreground mt-2">{points.length} of {missions.length} missions shown (missions without verified coordinates are hidden).</p>
    </div>
  );
}

function EmbassyFinder({ countries }) {
  const [represented, setRepresented] = useState("");
  const [host, setHost] = useState("");
  const [region, setRegion] = useState("");
  const [view, setView] = useState("list");
  const [results, setResults] = useState(null);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!represented && !host) { setResults(null); setMatch(null); return; }
    setLoading(true);
    const canMatch = represented && host && region.trim().length >= 2;
    const req = canMatch
      ? api.get("/visas/jurisdiction-match", { params: { represented, host, region: region.trim() } })
          .then((r) => { setMatch(r.data); setResults([...r.data.matched, ...r.data.others]); })
      : api.get("/visas/missions", { params: { represented: represented || undefined, host: host || undefined } })
          .then((r) => { setMatch(null); setResults(r.data.items); });
    req.catch(() => { setResults([]); setMatch(null); }).finally(() => setLoading(false));
  }, [represented, host, region]);

  const matchedIds = new Set((match?.matched || []).map((m) => m.id));

  return (
    <div data-testid="embassy-finder">
      <div className="border border-zinc-300 bg-white p-6 mb-6">
        <h2 className="font-heading font-700 text-lg mb-1">Find the right embassy or consulate</h2>
        <p className="text-sm text-muted-foreground mb-5">Select the country whose services you need and the country you are currently in. Add your state or province to see which consulate officially serves your region.</p>
        <div className="flex flex-wrap gap-4">
          <CountrySelect label="I need services from" value={represented} onChange={setRepresented} countries={countries} testid="finder-represented-select" />
          <CountrySelect label="I am currently in" value={host} onChange={setHost} countries={countries} testid="finder-host-select" />
          <label className="block flex-1 min-w-[180px]">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">My state / province (optional)</span>
            <input value={region} onChange={(e) => setRegion(e.target.value)} data-testid="finder-region-input"
              placeholder="e.g. California or CA"
              className="mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none" />
          </label>
        </div>
        {match && (
          <p className="text-xs text-muted-foreground mt-3" data-testid="jurisdiction-note">{match.note}</p>
        )}
      </div>
      {loading && <Spinner />}
      {!loading && results && (
        <>
          {match && match.matched.length > 0 && (
            <div className="border border-emerald-300 bg-emerald-50 px-4 py-3 mb-4 text-sm text-emerald-900 flex items-center justify-between flex-wrap gap-2" data-testid="jurisdiction-match-banner">
              <span><span className="font-700">{match.matched[0].name}</span> officially serves your region — shown first below.</span>
              <Link to={`/visas/regions/${host}/${encodeURIComponent(region.trim())}`} data-testid="region-page-link"
                className="font-700 text-emerald-800 underline whitespace-nowrap">All missions for {region.trim()} →</Link>
            </div>
          )}
          {match && match.matched.length === 0 && match.fallback_embassy && (
            <div className="border border-amber-300 bg-amber-50 px-4 py-3 mb-4 text-sm text-amber-900" data-testid="jurisdiction-fallback-banner">
              No consulate lists your region in its documented jurisdiction — the <span className="font-700">{match.fallback_embassy.name}</span> is the usual point of contact.
            </div>
          )}
          <div className="flex gap-1 mb-4">
            {[["list", "List", List], ["map", "Map", MapIcon]].map(([id, label, Icon]) => (
              <button key={id} onClick={() => setView(id)} data-testid={`finder-view-${id}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-700 border transition-colors ${view === id ? "bg-[#0e7490] border-[#0e7490] text-white" : "bg-white border-zinc-300 text-zinc-700"}`}>
                <Icon className="w-3.5 h-3.5" />{label}
              </button>
            ))}
          </div>
          {view === "map" ? (
            <MissionMap missions={results} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="finder-results">
              {results.map((m) => (
                <div key={m.id} className={matchedIds.has(m.id) ? "relative ring-2 ring-emerald-500" : ""} data-testid={matchedIds.has(m.id) ? `serves-region-${m.id}` : undefined}>
                  {matchedIds.has(m.id) && (
                    <span className="absolute -top-2.5 left-3 z-10 bg-emerald-600 text-white text-xs font-mono uppercase tracking-wide px-2 py-0.5">Serves your region</span>
                  )}
                  <MissionCard m={m} />
                </div>
              ))}
              {results.length === 0 && (
                <p className="text-sm text-muted-foreground col-span-full" data-testid="finder-no-results">
                  No missions found for this combination yet. Our diplomatic directory is expanding — check the represented country's Ministry of Foreign Affairs website for a full list.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function OfficialLinksGrid({ links, testidPrefix = "checker-link" }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {Object.entries(links || {}).map(([key, url]) => (
        <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-${key}`}
          className="flex items-center justify-between border border-zinc-200 px-4 py-2.5 text-sm hover:border-[#0e7490] transition-colors">
          <span className="font-600">{LINK_LABELS[key] || key}</span>
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
        </a>
      ))}
    </div>
  );
}
